/**
 * =============================================================================
 * IDS CRM Web - Support Dashboard Page
 * =============================================================================
 *
 * PURPOSE:
 * Comprehensive support dashboard showing ticket stats, SLA tracking,
 * engineer workload, and recent activity.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './SupportDashboard.css';

function SupportDashboard() {
  const { authFetch } = useAuth();

  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [criticalTickets, setCriticalTickets] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const [statsRes, recentRes, criticalRes, myRes] = await Promise.all([
        authFetch('/api/v1/tickets/dashboard'),
        authFetch('/api/v1/tickets?limit=5&open=true'),
        authFetch('/api/v1/tickets?priority=critical&open=true&limit=10'),
        authFetch(`/api/v1/tickets?my=true&open=true&limit=5`)
      ]);

      const statsData = await statsRes.json();
      const recentData = await recentRes.json();
      const criticalData = await criticalRes.json();
      const myData = await myRes.json();

      setStats(statsData);
      setRecentTickets(recentData.data || []);
      setCriticalTickets(criticalData.data || []);
      setMyTickets(myData.data || []);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchDashboard();
    // Refresh every 60 seconds
    const interval = setInterval(fetchDashboard, 60000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const getTimeSince = (date) => {
    if (!date) return '-';
    const mins = Math.floor((Date.now() - new Date(date)) / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'critical': return 'priority-critical';
      case 'high': return 'priority-high';
      case 'normal': return 'priority-normal';
      case 'low': return 'priority-low';
      default: return '';
    }
  };

  if (loading) {
    return <div className="loading-page">Loading dashboard...</div>;
  }

  return (
    <div className="support-dashboard">
      <div className="page-header">
        <div>
          <h1>Support Dashboard</h1>
          <p>Real-time overview of support operations</p>
        </div>
        <Link to="/tickets/new" className="btn-primary">
          + New Reactive Ticket
        </Link>
      </div>

      {/* Main Stats */}
      <div className="stats-grid">
        <div className="stat-card large">
          <div className="stat-icon open">T</div>
          <div className="stat-content">
            <div className="stat-value">{stats?.open_count || 0}</div>
            <div className="stat-label">Open Tickets</div>
          </div>
        </div>

        <div className="stat-card large">
          <div className="stat-icon progress">P</div>
          <div className="stat-content">
            <div className="stat-value">{stats?.in_progress_count || 0}</div>
            <div className="stat-label">In Progress</div>
          </div>
        </div>

        <div className={`stat-card large ${parseInt(stats?.critical_count) > 0 ? 'alert' : ''}`}>
          <div className="stat-icon critical">!</div>
          <div className="stat-content">
            <div className="stat-value">{stats?.critical_count || 0}</div>
            <div className="stat-label">Critical</div>
          </div>
        </div>

        <div className={`stat-card large ${parseInt(stats?.sla_response_breached) > 0 ? 'alert' : ''}`}>
          <div className="stat-icon sla">S</div>
          <div className="stat-content">
            <div className="stat-value">{stats?.sla_response_breached || 0}</div>
            <div className="stat-label">SLA Breached</div>
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="secondary-stats">
        <div className="mini-stat">
          <span className="mini-value">{stats?.high_count || 0}</span>
          <span className="mini-label">High Priority</span>
        </div>
        <div className="mini-stat">
          <span className="mini-value">{stats?.awaiting_customer_count || 0}</span>
          <span className="mini-label">Awaiting Customer</span>
        </div>
        <div className="mini-stat">
          <span className="mini-value">{stats?.awaiting_parts_count || 0}</span>
          <span className="mini-label">Awaiting Parts</span>
        </div>
        <div className="mini-stat">
          <span className="mini-value">{stats?.escalated_count || 0}</span>
          <span className="mini-label">Escalated</span>
        </div>
        <div className="mini-stat success">
          <span className="mini-value">{stats?.resolved_last_7_days || 0}</span>
          <span className="mini-label">Resolved (7d)</span>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Critical Tickets */}
        <div className="dashboard-section critical-section">
          <div className="section-header">
            <h2>Critical Tickets</h2>
            <Link to="/tickets?priority=critical" className="view-all">View all</Link>
          </div>
          {criticalTickets.length === 0 ? (
            <div className="empty-state success-state">
              No critical tickets
            </div>
          ) : (
            <div className="ticket-list">
              {criticalTickets.map(ticket => (
                <Link key={ticket.ticket_id} to={`/tickets/${ticket.ticket_id}`} className="ticket-item critical">
                  <div className="ticket-main">
                    <span className="ticket-ref">{ticket.ticket_ref}</span>
                    <span className="ticket-subject">{ticket.subject}</span>
                  </div>
                  <div className="ticket-meta">
                    <span className="ticket-customer">{ticket.customer_name}</span>
                    <span className="ticket-time">{getTimeSince(ticket.opened_at)}</span>
                    {(ticket.response_breached || ticket.resolution_breached) && (
                      <span className="sla-badge">SLA</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* My Work */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>My Work</h2>
            <Link to="/tickets?my=true" className="view-all">View all</Link>
          </div>
          {myTickets.length === 0 ? (
            <div className="empty-state">
              No reactive tickets assigned to you
            </div>
          ) : (
            <div className="ticket-list">
              {myTickets.map(ticket => (
                <Link key={ticket.ticket_id} to={`/tickets/${ticket.ticket_id}`} className="ticket-item">
                  <div className="ticket-main">
                    <span className={`priority-dot ${getPriorityClass(ticket.priority)}`} />
                    <span className="ticket-ref">{ticket.ticket_ref}</span>
                    <span className="ticket-subject">{ticket.subject}</span>
                  </div>
                  <div className="ticket-meta">
                    <span className="ticket-customer">{ticket.customer_name}</span>
                    <span className={`status-tag status-${ticket.status}`}>
                      {ticket.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent Tickets */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Recent Tickets</h2>
            <Link to="/tickets" className="view-all">View all</Link>
          </div>
          {recentTickets.length === 0 ? (
            <div className="empty-state">
              No open tickets
            </div>
          ) : (
            <div className="ticket-list">
              {recentTickets.map(ticket => (
                <Link key={ticket.ticket_id} to={`/tickets/${ticket.ticket_id}`} className="ticket-item">
                  <div className="ticket-main">
                    <span className={`priority-dot ${getPriorityClass(ticket.priority)}`} />
                    <span className="ticket-ref">{ticket.ticket_ref}</span>
                    <span className="ticket-subject">{ticket.subject}</span>
                  </div>
                  <div className="ticket-meta">
                    <span className="ticket-customer">{ticket.customer_name}</span>
                    <span className="ticket-time">{getTimeSince(ticket.opened_at)}</span>
                    <span className="ticket-assignee">{ticket.assigned_to_name || 'Unassigned'}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Quick Actions</h2>
          </div>
          <div className="quick-actions">
            <Link to="/tickets/new" className="action-button">
              + New Reactive Ticket
            </Link>
            <Link to="/tickets?open=true" className="action-button">
              Open Reactive Tickets
            </Link>
            <Link to="/tickets?my=true" className="action-button">
              My Work
            </Link>
            <Link to="/assets?tab=service" className="action-button">
              Due Service
            </Link>
            <Link to="/assets?tab=wear" className="action-button">
              Wear Alerts
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupportDashboard;
